import { useCallback } from "react";
import { tryAcquireScrollLock } from "../utils/scrollCoordinator";

export interface ScrollToElementOptions {
  /**
   * Scroll behavior: 'smooth' for animated scroll, 'auto' for instant
   * @default 'smooth'
   */
  behavior?: ScrollBehavior;

  /**
   * Vertical alignment when scrolling
   * @default 'center'
   */
  block?: ScrollLogicalPosition;

  /**
   * Whether to skip if element is already in view
   * @default true
   */
  skipIfInView?: boolean;

  /**
   * Whether to prevent duplicate scrolls to the same element
   * @default true
   */
  preventDuplicates?: boolean;

  /**
   * Container element ID to scroll within
   * @default 'annotator-pages-container'
   */
  containerId?: string;
}

const DEFAULT_OPTIONS: Required<ScrollToElementOptions> = {
  behavior: "smooth",
  block: "center",
  skipIfInView: true,
  preventDuplicates: true,
  containerId: "annotator-pages-container",
};

// Global state for duplicate prevention (shared across all hook instances)
let lastScrolledElement: string | null = null;

/**
 * Resets the global duplicate scroll prevention state.
 * Call this when the scroll target changes to allow re-scrolling to the same element.
 */
export const resetLastScrolledElement = () => {
  lastScrolledElement = null;
};

/**
 * Unified hook for scrolling to elements with conflict prevention and optimization.
 * Handles in-view detection, duplicate prevention, and scroll coordination.
 * Uses global state to prevent duplicate scrolls across all component instances.
 */
const useScrollToElement = () => {
  const scrollToElement = useCallback(
    (elementId: string, options?: ScrollToElementOptions): boolean => {
      const opts = { ...DEFAULT_OPTIONS, ...options };

      // Prevent duplicate scrolls (global check)
      if (opts.preventDuplicates && lastScrolledElement === elementId) {
        return false;
      }

      const container = document.getElementById(opts.containerId);
      const element = document.getElementById(elementId);

      if (!element || !container) {
        return false;
      }

      // Check if element is already in view
      if (opts.skipIfInView) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        const isInView =
          elementRect.top >= containerRect.top &&
          elementRect.bottom <= containerRect.bottom;

        if (isInView) {
          lastScrolledElement = elementId;
          return true; // Element in view, no scroll needed but considered successful
        }
      }

      // Try to acquire scroll lock for smooth scrolls to prevent conflicts
      if (opts.behavior === "smooth" && !tryAcquireScrollLock()) {
        // Another smooth scroll is in progress, skip this one
        return false;
      }

      // Perform the scroll
      element.scrollIntoView({
        behavior: opts.behavior,
        block: opts.block,
      });

      lastScrolledElement = elementId;
      return true;
    },
    [],
  );

  /**
   * Resets the duplicate scroll prevention for a specific element or all elements.
   */
  const resetScrollState = useCallback((elementId?: string) => {
    if (!elementId || lastScrolledElement === elementId) {
      lastScrolledElement = null;
    }
  }, []);

  return {
    scrollToElement,
    resetScrollState,
  };
};

export default useScrollToElement;
